import { useState } from 'react';
import { KeyRound, Loader2, LogIn } from 'lucide-react';
import { verifyCredential } from '@/api.ts';
import { Button } from '@/components/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Input, Label } from '@/components/ui/input.tsx';
import { Alert, AlertDescription } from '@/components/ui/alert.tsx';
import { beginSignIn, currentPath } from '@/lib/console-auth.ts';
import type { ConsoleAuthConfig } from '@/lib/console-auth.ts';
import { useSession } from '@/session.tsx';

/**
 * The way in.
 *
 * **Signing in with the pool is the path** (§7): one button, no ids to look up, and the role and
 * org list resolved afterwards from what the person actually has. The API-token form stays behind
 * a disclosure rather than being deleted, for two reasons that are not going away — an
 * environment deployed without a console pool has no other way in, and neither does a developer
 * running the console against a local control plane. When `config.json` names no pool, the
 * disclosure is the whole page.
 *
 * Neither credential is written anywhere: both live in React state and die with the tab
 * (§0.2 rule 2).
 */
export function SignIn({ authConfig }: { authConfig: ConsoleAuthConfig | null }) {
  const [error, setError] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [showToken, setShowToken] = useState(false);

  async function startSignIn() {
    if (leaving || !authConfig) return;
    setLeaving(true);
    setError(null);
    try {
      await beginSignIn(authConfig, currentPath());
    } catch (cause) {
      setLeaving(false);
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="bg-primary text-primary-foreground mb-2 grid size-9 place-items-center rounded-md font-mono text-sm">
            B
          </div>
          <CardTitle>Braid Cloud console</CardTitle>
          <CardDescription>
            {authConfig
              ? 'Sign in with your Braid Cloud account. Nothing is written to browser storage — closing this tab signs you out.'
              : 'This console is not configured for account sign-in. Use an organization id and an API token.'}
          </CardDescription>
        </CardHeader>

        <CardContent className="grid gap-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {authConfig && (
            <Button onClick={startSignIn} disabled={leaving}>
              {leaving ? <Loader2 className="animate-spin" /> : <LogIn />}
              {leaving ? 'Redirecting…' : 'Sign in'}
            </Button>
          )}

          {authConfig && !showToken && (
            <button
              type="button"
              onClick={() => setShowToken(true)}
              className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-4"
            >
              Use an API token instead
            </button>
          )}

          {(!authConfig || showToken) && <TokenForm />}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Phase 2's form, kept for machines and for environments with no pool.
 *
 * The verification is still the point. Without it the console accepts anything and the mistake
 * surfaces later as an unexplained 500 on whichever page loads first — a failure that looks like
 * the platform is broken rather than like a typo. `GET /me` also answers what the token may do,
 * so the rest of the console can show a viewer what they cannot change instead of letting them
 * find out from a 403.
 */
function TokenForm() {
  const { signIn } = useSession();
  const [orgId, setOrgId] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    try {
      const { role, org } = await verifyCredential(orgId.trim(), token.trim());
      signIn({ orgId: orgId.trim(), token: token.trim(), role, orgSlug: org.slug, orgName: org.name });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="grid gap-4 border-t pt-4" onSubmit={submit}>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-1.5">
        <Label htmlFor="orgId">Organization id</Label>
        <Input
          id="orgId"
          value={orgId}
          onChange={(event) => setOrgId(event.target.value)}
          placeholder="01J…"
          autoComplete="off"
          spellCheck={false}
          className="font-mono"
          required
        />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="token">API token</Label>
        <Input
          id="token"
          type="password"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          placeholder="bct_…"
          autoComplete="off"
          required
        />
        <p className="text-muted-foreground text-xs">
          Mint one with{' '}
          <code className="font-mono">npm run token:mint -- --org &lt;orgId&gt; --role owner</code>
        </p>
      </div>

      <Button type="submit" variant="outline" disabled={busy || !orgId.trim() || !token.trim()}>
        {busy ? <Loader2 className="animate-spin" /> : <KeyRound />}
        {busy ? 'Verifying…' : 'Continue with a token'}
      </Button>
    </form>
  );
}
